import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Loader2, CheckCircle2, FileText, ThumbsUp, CalendarClock,
  CalendarCheck, ChevronRight, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";

const CAL_URL = "https://cal.com/andrey-sebastian-qb99os/you-re-in-the-system";

const STAGES = [
  {
    key: "new",
    step: 1,
    label: "You're in the System",
    sub: "Your application has been received and is under review by our recruiting team.",
    Icon: CheckCircle2,
  },
  {
    key: "documents_needed",
    step: 2,
    label: "Action Needed: Send Documents",
    sub: "We need your CDL and Medical Certificate to move your application forward. Call us or email contact@hylascompanion.com.",
    Icon: FileText,
  },
  {
    key: "approved",
    step: 3,
    label: "You've Been Approved",
    sub: "You've cleared our initial review. Sit tight — the final step is coming.",
    Icon: ThumbsUp,
  },
  {
    key: "schedule_call",
    step: 4,
    label: "Schedule a Call with Hiring Manager",
    sub: "You're ready to roll. Book your 15-minute onboarding call and let's get you in a truck.",
    Icon: CalendarClock,
  },
];

function stageIndex(status) {
  const i = STAGES.findIndex((s) => s.key === status);
  return i === -1 ? 0 : i;
}

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/* ─── Progress Stepper ─── */
function Stepper({ status }) {
  const currentIdx = stageIndex(status);

  return (
    <div className="space-y-0">
      {STAGES.map((stage, i) => {
        const Icon = stage.Icon;
        const done = i < currentIdx;
        const active = i === currentIdx;
        const future = i > currentIdx;

        return (
          <motion.div
            key={stage.key}
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1, duration: 0.3 }}
            data-testid={`stage-${stage.key}`}
            className={`relative flex gap-5 pb-8 last:pb-0 ${future ? "opacity-35" : ""}`}
          >
            {/* Connector line */}
            {i < STAGES.length - 1 && (
              <div
                className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${
                  done ? "bg-primary" : "bg-border"
                }`}
              />
            )}

            {/* Circle */}
            <div
              className={`relative z-10 flex-shrink-0 w-10 h-10 flex items-center justify-center border-2 transition-all duration-300 ${
                done
                  ? "bg-primary border-primary"
                  : active
                  ? "bg-[#0A0A0A] border-primary"
                  : "bg-white border-border"
              }`}
            >
              {done ? (
                <CheckCircle2 className="w-5 h-5 text-black" strokeWidth={2.5} />
              ) : (
                <Icon
                  className={`w-5 h-5 ${active ? "text-primary" : "text-muted-foreground"}`}
                  strokeWidth={1.5}
                />
              )}

              {/* Pulse ring for active stage */}
              {active && (
                <span className="absolute inset-0 border-2 border-primary animate-ping opacity-30" />
              )}
            </div>

            {/* Content */}
            <div className="pt-1.5 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p
                  className={`font-head text-sm md:text-base uppercase tracking-[0.1em] ${
                    active ? "text-[#0A0A0A]" : done ? "text-[#0A0A0A]/70" : "text-[#0A0A0A]/40"
                  }`}
                >
                  {stage.label}
                </p>
                {active && (
                  <span className="font-head text-[10px] uppercase tracking-[0.2em] bg-primary text-black px-2.5 py-0.5">
                    Current
                  </span>
                )}
                {done && (
                  <span className="font-head text-[10px] uppercase tracking-[0.2em] text-primary">
                    ✓ Done
                  </span>
                )}
              </div>
              {(active || done) && (
                <p className="mt-1.5 text-sm text-foreground/60 leading-relaxed max-w-md">
                  {stage.sub}
                </p>
              )}

              {/* CTA for final stage */}
              {stage.key === "schedule_call" && active && (
                <motion.a
                  href={CAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="schedule-call-btn"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-5 inline-flex items-center gap-3 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-7 py-4 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300 group"
                >
                  <CalendarCheck className="w-5 h-5" />
                  Book Your Call
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ─── Result Card ─── */
function ResultCard({ data }) {
  const stage = STAGES.find((s) => s.key === data.status) || STAGES[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="status-result"
      className="mt-8"
    >
      {/* Driver header */}
      <div className="bg-[#0A0A0A] p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="font-head text-xs uppercase tracking-[0.3em] text-primary mb-2">Application Status</p>
            <h2 className="font-head text-2xl md:text-3xl uppercase tracking-[0.1em] text-white">
              Hey, {data.first_name} —
            </h2>
            <p className="text-white/60 mt-1">here's exactly where you stand.</p>
          </div>
          <div className="text-right">
            {data.position && (
              <div>
                <p className="font-head text-[10px] uppercase tracking-[0.25em] text-white/40">Applied For</p>
                <p className="font-head text-sm uppercase tracking-[0.1em] text-white mt-1">
                  {data.position}
                </p>
              </div>
            )}
            {data.created_at && (
              <div className="mt-3">
                <p className="font-head text-[10px] uppercase tracking-[0.25em] text-white/40">Applied On</p>
                <p className="text-white/60 text-sm mt-0.5">{fmt(data.created_at)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Stage banner */}
        <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-3">
          <stage.Icon className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="font-head text-sm uppercase tracking-[0.15em] text-primary">
            Stage {stage.step} of {STAGES.length}: {stage.label}
          </span>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white border border-border p-6 md:p-8">
        <p className="font-head text-xs uppercase tracking-[0.3em] text-muted-foreground mb-8">Progress</p>
        <Stepper status={data.status} />
      </div>

      <p className="mt-4 text-xs text-muted-foreground text-center">
        Questions? Call us at <span className="font-medium">(708) 395-9565</span> or email{" "}
          <span className="font-medium">contact@hylascompanion.com</span>
      </p>
    </motion.div>
  );
}

/* ─── Main page ─── */
export default function Track() {
  const location = useLocation();
  const fromApply = location.state?.fresh === true;
  const [query, setQuery] = useState(location.state?.phone || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Auto-trigger lookup when arriving from Apply form
  useEffect(() => {
    if (fromApply && location.state?.phone) {
      const timer = setTimeout(() => {
        const q = location.state.phone.trim();
        setLoading(true);
        setError("");
        api.trackApplication(q)
          .then((data) => setResult(data))
          .catch(() =>
            setError(
              "Your application is being processed — it should appear here within a moment. Try searching again shortly."
            )
          )
          .finally(() => setLoading(false));
      }, 900);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await api.trackApplication(q);
      setResult(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(
          "No application found with that email or phone. Double-check and try again, or call (708) 395-9565."
        );
      } else if (err?.response?.status === 400) {
        setError(err?.response?.data?.detail || "Please enter a valid email or phone number.");
      } else {
        setError("Something went wrong. Please try again or call us directly.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="track-page" className="min-h-screen">
      <SEOHead
        title="Track Your Driver Application | Hylas Companion"
        description="Check the status of your CDL truck driver application at Hylas Companion. Enter your email or phone number to see exactly where you stand in the hiring process."
        keywords="track truck driver application, driver application status, CDL job application tracking, Hylas Companion application"
        path="/track"
        noindex={false}
      />
      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <span className="font-head text-xs uppercase tracking-[0.4em] text-primary">Driver Portal</span>
          <h1 className="font-head text-4xl md:text-6xl font-bold uppercase tracking-[0.12em] mt-5 text-white leading-tight">
            Track Your Application
          </h1>
          <p className="mt-5 text-white/60 text-base md:text-lg max-w-md mx-auto">
            Enter the email or phone number you used when you applied. We'll show you exactly where things stand.
          </p>

          {/* Just-submitted welcome banner */}
          {fromApply && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-6 flex items-center gap-3 bg-primary/10 border border-primary/30 px-5 py-3 max-w-md mx-auto"
            >
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
              <p className="text-sm text-white/80 text-left">
                Application received. Your phone number is pre-filled — hit <span className="text-primary font-medium">Check Status</span> to see your place in the pipeline.
              </p>
            </motion.div>
          )}

          {/* Lookup form */}
          <form
            onSubmit={lookup}
            data-testid="track-form"
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                data-testid="track-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Email address or phone number"
                className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 pl-12 pr-4 py-4 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <button
              type="submit"
              data-testid="track-submit"
              disabled={loading || !query.trim()}
              className="font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2 shrink-0"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Check Status"}
            </button>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-2xl mx-auto px-6 pb-24">
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="track-error"
              className="mt-8 flex items-start gap-4 border-2 border-destructive/30 bg-destructive/5 p-5"
            >
              <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/80">{error}</p>
            </motion.div>
          )}
          {result && <ResultCard key="result" data={result} />}
        </AnimatePresence>
      </section>
    </div>
  );
}
